import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ContraAccount } from '../../interfaces/accounting.interface';
import { JournalEntryService } from '../../services/journal-entry.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import SearchableSelect from '../common/SearchableSelect';
import { toLocalIsoDate } from '../../utils/dateUtils';

export const contraAccountLabel = (a: ContraAccount): string =>
    `${a.code} - ${a.name}${a.kind === 'BANK' ? ' (Bank)' : ''}`;

const ContraVoucherCreate: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const [accounts, setAccounts] = useState<ContraAccount[]>([]);
    const [fromId, setFromId] = useState('');
    const [toId, setToId] = useState('');
    const [amount, setAmount] = useState('');
    const [date, setDate] = useState(toLocalIsoDate());
    const [reference, setReference] = useState('');
    const [narration, setNarration] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        JournalEntryService.getContraAccounts(institutionId)
            .then(setAccounts)
            .catch(err => toast.showApiError(err, 'Failed to load cash and bank accounts'));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const sameAccount = !!fromId && fromId === toId;
    const amountNum = Number(amount);
    const amountValid = Number.isFinite(amountNum) && amountNum > 0;
    const canSubmit = !saving && !!fromId && !!toId && !sameAccount && amountValid && !!date;

    // Keep the other side's choice out of each list so the same account can't be picked twice.
    const fromOptions = useMemo(() => accounts.filter(a => a.id !== toId), [accounts, toId]);
    const toOptions = useMemo(() => accounts.filter(a => a.id !== fromId), [accounts, fromId]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (sameAccount) {
            toast.showError('From and To accounts must be different');
            return;
        }
        if (!canSubmit) return;
        setSaving(true);
        try {
            const created = await JournalEntryService.createContra({
                institutionId: AuthService.getSelectedInstitution() as string,
                fromAccountId: fromId,
                toAccountId: toId,
                amount: amountNum,
                date,
                reference: reference.trim() || undefined,
                narration: narration.trim() || undefined,
            });
            toast.showSuccess('Contra voucher posted');
            navigate(created?.id ? `/accounting/journal-entries/${created.id}` : '/accounting/journal-entries');
        } catch (err) {
            toast.showApiError(err, 'Failed to create contra voucher');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-help-page="accounting.contra.page" style={{ padding: '20px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>Contra Voucher</h2>
                <Button data-help="accounting.contra.back" variant="outlined" onClick={() => navigate('/accounting/journal-entries')}>
                    Back to List
                </Button>
            </div>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
                Transfer between cash/bank accounts. The entry is posted immediately.
            </Typography>
            <form onSubmit={handleSubmit}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                    <Box sx={{ flex: '1 1 280px', maxWidth: 420 }}>
                        <SearchableSelect<ContraAccount>
                            data-help="accounting.contra.field.from"
                            label="From account"
                            required
                            options={fromOptions}
                            value={fromId}
                            onChange={id => setFromId(id)}
                            getOptionLabel={contraAccountLabel}
                        />
                    </Box>
                    <Box sx={{ flex: '1 1 280px', maxWidth: 420 }}>
                        <SearchableSelect<ContraAccount>
                            data-help="accounting.contra.field.to"
                            label="To account"
                            required
                            options={toOptions}
                            value={toId}
                            onChange={id => setToId(id)}
                            getOptionLabel={contraAccountLabel}
                            error={sameAccount}
                        />
                    </Box>
                </Box>
                {sameAccount && (
                    <Alert severity="error" sx={{ mb: 2 }}>From and To accounts must be different.</Alert>
                )}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                    <TextField data-help="accounting.contra.field.amount"
                        label="Amount"
                        type="number"
                        required
                        value={amount}
                        onChange={e => setAmount(e.target.value)}
                        inputProps={{ min: 0, step: 'any' }}
                        sx={{ flex: '1 1 160px', maxWidth: 220 }}
                    />
                    <TextField data-help="accounting.contra.field.date"
                        label="Date"
                        type="date"
                        required
                        value={date}
                        onChange={e => setDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ flex: '1 1 160px', maxWidth: 220 }}
                    />
                    <TextField data-help="accounting.contra.field.reference"
                        label="Reference"
                        value={reference}
                        onChange={e => setReference(e.target.value)}
                        sx={{ flex: '1 1 200px', maxWidth: 320 }}
                    />
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                    <TextField data-help="accounting.contra.field.narration"
                        label="Narration"
                        multiline
                        minRows={2}
                        value={narration}
                        onChange={e => setNarration(e.target.value)}
                        sx={{ flex: '1 1 320px', maxWidth: 640 }}
                    />
                </Box>
                <Button data-help="accounting.contra.save" type="submit" variant="contained" disabled={!canSubmit}>
                    {saving ? 'Saving...' : 'Post contra voucher'}
                </Button>
            </form>
        </div>
    );
};

export default ContraVoucherCreate;
